import type { MediaRole } from '@/shared/api/types';

/**
 * Lo que la pantalla de captura le deja a la de declaración.
 *
 * Vive en un módulo y no en el estado del router: las fotos son `File` de varios megabytes y el
 * historial del navegador no es lugar para eso — cada entrada se serializa y se guarda en disco.
 * Acá duran lo que dura la navegación, que es exactamente lo que hace falta.
 */
export interface PendingCapture {
  /**
   * Cero o más imágenes, por RF-10.
   *
   * Era una sola. Un plato entra mal en una foto: el modelo estima mejor con la vista de arriba y
   * una de costado, y un envase suele necesitar el frente y la tabla nutricional. Guardar una y
   * pisar la anterior obligaba a elegir cuál de las dos servía más.
   */
  files: File[];
  role: MediaRole;
  ean: string | null;
  previewUrls: string[];
}

let pending: PendingCapture | null = null;

export const captureStore = {
  set(capture: PendingCapture): void {
    captureStore.clear();
    pending = capture;
  },

  /** Suma una imagen a la captura en curso, conservando las que ya estaban. */
  add(file: File, role: MediaRole): void {
    const url = URL.createObjectURL(file);

    pending =
      pending === null
        ? { files: [file], role, ean: null, previewUrls: [url] }
        : { ...pending, files: [...pending.files, file], previewUrls: [...pending.previewUrls, url] };
  },

  /** Quita una imagen por posición y libera su previsualización. */
  removeAt(index: number): void {
    if (pending === null) {
      return;
    }

    const url = pending.previewUrls[index];

    if (url !== undefined) {
      URL.revokeObjectURL(url);
    }

    pending = {
      ...pending,
      files: pending.files.filter((_, position) => position !== index),
      previewUrls: pending.previewUrls.filter((_, position) => position !== index),
    };
  },

  /** Devuelve la captura y la deja en su lugar: la pantalla puede volver a leerla al re-renderizar. */
  peek(): PendingCapture | null {
    return pending;
  },

  clear(): void {
    // Sin esto los blobs quedan retenidos hasta que se recarga la página.
    pending?.previewUrls.forEach((url) => URL.revokeObjectURL(url));

    pending = null;
  },
};
